import { useCallback, useEffect, useRef, useState } from "react";
import { zboxLegalDbFetch } from "../..";
import styles from "./InfringementChecker.module.scss";
import LoadingScreen from "../../components/loading_screen/LoadingScreen";
import Checkbox from "../../components/checkbox/Checkbox";

interface IGetZboxCompetitorListingMappingsResult {
    amountLeft: number;
    pageNumber: number;
    pageSize: number;
    results: IZboxCompetitorListingMapping[];
}

interface IZboxCompetitorListingMapping {
    zboxListingRecordId: number;
    zboxListingRecord: IListingRecord;
    competitorListingRecordId: number;
    competitorListingRecord: IListingRecord;
    hasInfringingEvidence: boolean | null;
}

interface IListingRecord {
    id: number;
    batchId: number;
    title: string | null;
    listingId: string;
    screenshotDate: string; //DateTimeOffset
    screenshotFilename: string;
    zboxSku: string | null;
    isZboxListing: boolean;
    screenshotData?: string; // This is a base64 string
}

interface IGetZboxCompetitorListingMappingsProps {
    newIndex?: number;
    updatedZboxCompetitorListingMappings?: IZboxCompetitorListingMapping[];
    loadInBackground: boolean;
    filters?: IGetZboxCompetitorListingMappingsFilters;
    reset?: boolean;
}

interface IGetZboxCompetitorListingMappingsFilters {
    pageNumber: number;
    pageSize: number;
    hasInfringingEvidenceTrue: boolean;
    hasInfringingEvidenceFalse: boolean;
    hasInfringingEvidenceNull: boolean;
}

const InfringementChecker = () => {
    const [amountLeft, setAmountLeft] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [zboxCompetitorListingMappings, setZboxCompetitorListingMappings] = useState<IZboxCompetitorListingMapping[]>([]);
    const [currentZboxCompetitorListingMappingIndex, setCurrentZboxCompetitorListingMappingIndex] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [backgroundLoading, setBackgroundLoading] = useState<boolean>(false);

    const formRef = useRef<HTMLFormElement>(null);
    const filtersFormRef = useRef<HTMLFormElement>(null);
    const preventLoad = useRef<boolean>(false);
    const endOfListReached = useRef<boolean>(false);

    const pageSize = 10;
    const mappingsMaxLength = 50;

    const activeZboxCompetitorListingMapping = zboxCompetitorListingMappings
        .find((_, index) => index === currentZboxCompetitorListingMappingIndex);

    const getZboxCompetitorListingMappingsAsync = useCallback(async (props?: IGetZboxCompetitorListingMappingsProps) => {
        if (loading) return;
        try {
            switch (props?.loadInBackground ?? false) {
                case true:
                    setBackgroundLoading(true);
                    break;
                case false:
                    setLoading(true);
                    break;
            }

            let newIndex = props?.newIndex ?? currentZboxCompetitorListingMappingIndex
            setCurrentZboxCompetitorListingMappingIndex(newIndex);

            if (endOfListReached.current && !props?.reset) {
                if (props?.updatedZboxCompetitorListingMappings)
                    setZboxCompetitorListingMappings(props.updatedZboxCompetitorListingMappings);
                return;
            }

            if (preventLoad.current) return;
            preventLoad.current = true;

            const getZboxCompetitorListingMappingsFilters: IGetZboxCompetitorListingMappingsFilters = {
                pageNumber: props?.filters?.pageNumber ?? pageNumber,
                pageSize: props?.filters?.pageSize ?? pageSize,
                hasInfringingEvidenceTrue: props?.filters?.hasInfringingEvidenceTrue ?? filtersFormRef.current?.hasInfringingEvidenceTrue.checked ?? false,
                hasInfringingEvidenceFalse: props?.filters?.hasInfringingEvidenceFalse ?? filtersFormRef.current?.hasInfringingEvidenceFalse.checked ?? false,
                hasInfringingEvidenceNull: props?.filters?.hasInfringingEvidenceNull ?? filtersFormRef.current?.hasInfringingEvidenceNull.checked ?? false,
            };

            const response = await zboxLegalDbFetch(`api/GetZboxCompetitorListingMappingsAsync`, {
                method: "POST",
                body: JSON.stringify(getZboxCompetitorListingMappingsFilters),
                headers: {
                    "Content-Type": "application/json",
                },
            });
            if (response.status !== 200) throw new Error(`${response.status}: ${response.statusText}`);

            const data = await response.json() as IGetZboxCompetitorListingMappingsResult;
            if (data.results.length < 1) {
                endOfListReached.current = true;
                return;
            }
            else {
                let mappings = props?.updatedZboxCompetitorListingMappings
                    ? props.updatedZboxCompetitorListingMappings.concat(data.results)
                    : zboxCompetitorListingMappings.concat(data.results);

                setAmountLeft(data.amountLeft);
                setPageNumber(props?.filters?.pageNumber ? props.filters.pageNumber + 1 : data.pageNumber + 1);
                setZboxCompetitorListingMappings(mappings);
            }
        }
        catch (error: any) {
            alert(error.message);
        }
        finally {
            setLoading(false);
            setBackgroundLoading(false);
            preventLoad.current = false;
        }
    }, [loading, pageNumber, zboxCompetitorListingMappings, currentZboxCompetitorListingMappingIndex]);

    useEffect(() => {
        (async () => {
            if (!filtersFormRef.current) return;
            await getZboxCompetitorListingMappingsAsync({
                loadInBackground: false,
                updatedZboxCompetitorListingMappings: [],
                filters: {
                    pageNumber: 1,
                    pageSize: pageSize,
                    hasInfringingEvidenceTrue: filtersFormRef.current.hasInfringingEvidenceTrue.checked,
                    hasInfringingEvidenceFalse: filtersFormRef.current.hasInfringingEvidenceFalse.checked,
                    hasInfringingEvidenceNull: filtersFormRef.current.hasInfringingEvidenceNull.checked,
                },
            });
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (zboxCompetitorListingMappings.length > mappingsMaxLength) {
            let mappings = [...zboxCompetitorListingMappings];
            mappings = mappings.slice(pageSize); // Remove the first pageSize elements
            const newIndex = currentZboxCompetitorListingMappingIndex - (pageSize);
            setZboxCompetitorListingMappings(mappings);
            setCurrentZboxCompetitorListingMappingIndex(newIndex);
        }
    }, [zboxCompetitorListingMappings, currentZboxCompetitorListingMappingIndex]);

    const setZboxCompetitorListingMappingInfringementAsync = useCallback(async (hasInfringingEvidence: boolean) => {
        if (loading) return;
        if (activeZboxCompetitorListingMapping === undefined) return;
        if (!formRef.current) return;
        try {
            setLoading(true);
            const formData = {
                competitorListingRecordId: activeZboxCompetitorListingMapping.competitorListingRecordId,
                zboxListingRecordId: activeZboxCompetitorListingMapping.zboxListingRecordId,
                hasInfringingEvidence: hasInfringingEvidence,
            };
            const response = await zboxLegalDbFetch(`api/SetZboxCompetitorListingMappingInfringementAsync`, {
                method: "POST",
                body: JSON.stringify(formData),
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.status === 200) {
                const updatedZboxCompetitorListingMappings = zboxCompetitorListingMappings.map((zboxCompetitorListingMapping, index) => {
                    if (index === currentZboxCompetitorListingMappingIndex) {
                        return {
                            ...zboxCompetitorListingMapping,
                            hasInfringingEvidence: hasInfringingEvidence,
                        };
                    }
                    return zboxCompetitorListingMapping;
                });
                let newIndex = currentZboxCompetitorListingMappingIndex + 1;
                if (newIndex >= zboxCompetitorListingMappings.length) newIndex = zboxCompetitorListingMappings.length - 1;
                const distanceToEnd = zboxCompetitorListingMappings.length - newIndex;
                if (newIndex >= distanceToEnd && !preventLoad.current) {
                    getZboxCompetitorListingMappingsAsync({
                        newIndex: newIndex,
                        updatedZboxCompetitorListingMappings: updatedZboxCompetitorListingMappings,
                        loadInBackground: true,
                    });
                }
                else {
                    setZboxCompetitorListingMappings(updatedZboxCompetitorListingMappings);
                    setCurrentZboxCompetitorListingMappingIndex(newIndex);
                }
            }
            else {
                switch (response.status) {
                    case 401:
                        throw new Error(`${response.status}: Unauthorized. Token has most likely expired and hasn't been refreshed.`);
                    default:
                        throw new Error(`${response.status}: ${response.statusText}`);
                }
            }
        }
        catch (error: any) {
            alert(error.message)
        }
        finally {
            setLoading(false);
        }
    }, [loading, currentZboxCompetitorListingMappingIndex, zboxCompetitorListingMappings, getZboxCompetitorListingMappingsAsync, activeZboxCompetitorListingMapping]);

    useEffect(() => {
        const keyDownEventListener = (e: Event) => {
            e.preventDefault();
            if (loading) return;
            const keyboardEvent = e as KeyboardEvent;
            if (keyboardEvent.key === "ArrowLeft") {
                let newIndex = currentZboxCompetitorListingMappingIndex - 1;
                if (newIndex < 0) newIndex = 0;
                setCurrentZboxCompetitorListingMappingIndex(newIndex);
            }
            else if (keyboardEvent.key === "ArrowRight") {
                let newIndex = currentZboxCompetitorListingMappingIndex + 1;
                if (newIndex >= zboxCompetitorListingMappings.length) newIndex = zboxCompetitorListingMappings.length - 1;
                const distanceToEnd = zboxCompetitorListingMappings.length - newIndex;
                if (newIndex >= distanceToEnd && !preventLoad.current) {
                    getZboxCompetitorListingMappingsAsync({
                        newIndex: newIndex,
                        loadInBackground: true,
                    });
                }
                else {
                    setCurrentZboxCompetitorListingMappingIndex(newIndex);
                }
            }
            else if (keyboardEvent.key === "ArrowDown") {
                (async () => {
                    await setZboxCompetitorListingMappingInfringementAsync(false);
                })();
            }
            else if (keyboardEvent.key === "ArrowUp") {
                (async () => {
                    await setZboxCompetitorListingMappingInfringementAsync(true);
                })();
            }
        };

        document.addEventListener("keydown", keyDownEventListener);
        return () => {
            document.removeEventListener("keydown", keyDownEventListener);
        };
    }, [loading, currentZboxCompetitorListingMappingIndex, zboxCompetitorListingMappings, setZboxCompetitorListingMappingInfringementAsync, getZboxCompetitorListingMappingsAsync, backgroundLoading]);

    const byteArrayToUrl = (base64String: string) => `data:image/png;base64,${base64String}`;

    const simulateKeyDown = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, key: string) => {
        e.preventDefault();
        const event = new KeyboardEvent('keydown', { key: key }) as KeyboardEvent;
        document.dispatchEvent(event);
    };

    const handleFiltersFormSubmitAsync = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!filtersFormRef.current) return;
        await getZboxCompetitorListingMappingsAsync({
            loadInBackground: false,
            updatedZboxCompetitorListingMappings: [],
            newIndex: 0,
            reset: true,
            filters: {
                pageNumber: 1,
                pageSize: pageSize,
                hasInfringingEvidenceTrue: filtersFormRef.current.hasInfringingEvidenceTrue.checked,
                hasInfringingEvidenceFalse: filtersFormRef.current.hasInfringingEvidenceFalse.checked,
                hasInfringingEvidenceNull: filtersFormRef.current.hasInfringingEvidenceNull.checked,
            },
        });
    };

    return <div className={styles.container}>
        {
            loading ? <LoadingScreen fullscreen /> : <></>
        }

        <div className={styles.titleContainer}>
            <h2>Infringement Checker</h2>
            <div>Amount Remaining: {amountLeft.toLocaleString('en-US')}</div>
            <div className={styles.keyboardControlsContainer}>
                <button onClick={e => simulateKeyDown(e, "ArrowUp")} className={styles.keyboardKey}>{"↑"} INFRINGING</button>
                <button onClick={e => simulateKeyDown(e, "ArrowDown")} className={styles.keyboardKey}>{"↓"} NOT INFRINGING</button>
                <button onClick={e => simulateKeyDown(e, "ArrowLeft")} className={styles.keyboardKey}>{"←"} PREVIOUS</button>
                <button onClick={e => simulateKeyDown(e, "ArrowRight")} className={styles.keyboardKey}>{"→"} NEXT</button>
            </div>
        </div>

        <form className={styles.filtersContainer} ref={filtersFormRef} onSubmit={async e => await handleFiltersFormSubmitAsync(e)}>
            <div>
                Filters: <button className={styles.applyFiltersButton} type="submit">Apply Filters</button>
            </div>
            <div className={styles.filtersGroup}>
                <div className={styles.filtersGroupTitle}>Has Infringing Evidence:</div>
                <div className={styles.filtersGroupRow}>
                    <Checkbox
                        id="hasInfringingEvidenceTrue"
                        name="hasInfringingEvidenceTrue"
                        defaultChecked={false}
                        label="True"
                    />

                    <Checkbox
                        id="hasInfringingEvidenceFalse"
                        name="hasInfringingEvidenceFalse"
                        defaultChecked={false}
                        label="False"
                    />

                    <Checkbox
                        id="hasInfringingEvidenceNull"
                        name="hasInfringingEvidenceNull"
                        defaultChecked={true}
                        label="Null"
                    />
                </div>
            </div>
        </form>

        <div className={styles.checkedStatusContainer}>
            <div>
                Already checked: {activeZboxCompetitorListingMapping?.hasInfringingEvidence === null
                    ? <b style={{ color: "red" }}>NO</b>
                    : <b style={{ color: "green" }}>YES</b>
                }
            </div>
            <div>|</div>
            <div>
                Has Infringing Evidence: {(() => {
                    switch (activeZboxCompetitorListingMapping?.hasInfringingEvidence) {
                        case true:
                            return <b style={{ color: "green" }}>TRUE</b>;
                        case false:
                            return <b style={{ color: "red" }}>FALSE</b>;
                        default:
                            return <b style={{ color: "gray" }}>NULL</b>;
                    }
                })()}
            </div>
        </div>
        {
            !activeZboxCompetitorListingMapping ? <></> : (
                <form ref={formRef} className={styles.form}>
                    <div className={styles.imageComparisonContainer}>
                        <div className={styles.imageContainer}>
                            <div><b>Competitor</b></div>
                            <img src={
                                activeZboxCompetitorListingMapping.competitorListingRecord.screenshotData
                                    ? byteArrayToUrl(activeZboxCompetitorListingMapping.competitorListingRecord.screenshotData)
                                    : activeZboxCompetitorListingMapping.competitorListingRecord.screenshotFilename
                            } alt="" />
                        </div>
                        <div className={styles.imageContainer}>
                            <div><b>Zbox</b></div>
                            <img src={
                                activeZboxCompetitorListingMapping.zboxListingRecord.screenshotData
                                    ? byteArrayToUrl(activeZboxCompetitorListingMapping.zboxListingRecord.screenshotData)
                                    : activeZboxCompetitorListingMapping.zboxListingRecord.screenshotFilename
                            } alt="" />
                        </div>
                    </div>
                </form>
            )
        }
    </div>;
}

export default InfringementChecker;